import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchCompanyByIdentifier, type PublicCompany } from '../services/companyService';

interface CompanyContextData {
  company: PublicCompany | null;
  isLoading: boolean;
  error: Error | null;
}

const CompanyContext = createContext<CompanyContextData | undefined>(undefined);

interface CompanyProviderProps {
  children: ReactNode;
}

// Multi-tenant: extrair identificador da empresa (slug ou subdomain)
const getCompanyIdentifier = (): string => {
  const hostname = window.location.hostname;
  
  // Desenvolvimento local - usa ENV
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_DEV_COMPANY_SLUG || 'better';
  }
  
  // Produção - extrai subdomain
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    return parts[0]; // subdomain
  }
  
  // Fallback
  return 'better';
};

export function CompanyProvider({ children }: CompanyProviderProps) {
  const [company, setCompany] = useState<PublicCompany | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadCompany = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const identifier = getCompanyIdentifier();
        const data = await fetchCompanyByIdentifier(identifier);
        
        if (!data) {
          throw new Error('Empresa não encontrada');
        }
        
        setCompany(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Erro ao carregar empresa'));
        console.error('Erro ao carregar empresa:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadCompany();
  }, []);

  return (
    <CompanyContext.Provider value={{ company, isLoading, error }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany(): CompanyContextData {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return context;
}
